import { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import { useImplantFrozenFreshStrawEmbryoMutation } from '@/app/services/freshStrawRest';
import { addToast } from '@/app/slices/toast';
import { StringMapDropdown } from '@/components/common/string-map-dropdown';
import {
  StringMapAttributeName,
  StringMapObjectTypeCode,
} from '@/components/common/string-map-dropdown/constants';
import { YardsAutoComplete } from '@/components/common/yards-auto-complete';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface FreshStrawImplantFrozenDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  embryoId: string | null;
  embryoLabel?: string;
  transferDate: string;
  transferSiteId: string;
  defaultFarmLocationId?: string;
  onImplanted?: () => void;
}

export function FreshStrawImplantFrozenDialog({
  open,
  onOpenChange,
  embryoId,
  embryoLabel,
  transferDate,
  transferSiteId,
  defaultFarmLocationId,
  onImplanted,
}: FreshStrawImplantFrozenDialogProps) {
  const dispatch = useDispatch();

  const [newStrawNumber, setNewStrawNumber] = useState('');
  const [calfType, setCalfType] = useState<number | null>(null);
  const [farmLocationId, setFarmLocationId] = useState<string | undefined>(
    defaultFarmLocationId,
  );
  const [ehmDate, setEhmDate] = useState('');
  const [strawLotNo, setStrawLotNo] = useState('');
  const [strawIncubatorNo, setStrawIncubatorNo] = useState('');
  const [thawThermosTemp, setThawThermosTemp] = useState('');

  const [implantFrozen, { isLoading }] =
    useImplantFrozenFreshStrawEmbryoMutation();

  useEffect(() => {
    if (open) {
      setNewStrawNumber('');
      setCalfType(null);
      setFarmLocationId(defaultFarmLocationId);
      setEhmDate('');
      setStrawLotNo('');
      setStrawIncubatorNo('');
      setThawThermosTemp('');
    }
  }, [open, defaultFarmLocationId]);

  const canSubmit =
    !!embryoId &&
    !!transferDate &&
    !!transferSiteId &&
    newStrawNumber.trim() !== '' &&
    calfType !== null &&
    ehmDate.trim() !== '';

  const handleSubmit = async () => {
    if (!canSubmit || !embryoId) return;
    const strawNum = Number.parseInt(newStrawNumber, 10);
    if (Number.isNaN(strawNum)) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Invalid straw number',
          detail: 'Straw number must be a whole number.',
          life: ToastLife.LONG,
        }),
      );
      return;
    }
    try {
      await implantFrozen({
        embryoId,
        transferDate,
        transferSiteId,
        newStrawNumber: strawNum,
        calfType: calfType!,
        farmLocationId: farmLocationId || undefined,
        ehmDate,
        strawLotNo: strawLotNo || undefined,
        strawIncubatorNo: strawIncubatorNo || undefined,
        thawThermosTemp: thawThermosTemp || undefined,
      }).unwrap();

      dispatch(
        addToast({
          severity: 'success',
          summary: 'Embryo implanted',
          detail: embryoLabel
            ? `${embryoLabel} marked as implanted.`
            : 'Embryo marked as implanted.',
          life: ToastLife.SHORT,
        }),
      );
      onImplanted?.();
      onOpenChange(false);
    } catch (err) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Implant failed',
          detail: err instanceof Error ? err.message : String(err),
          life: ToastLife.LONG,
        }),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="md:max-w-lg">
        <DialogHeader>
          <DialogTitle>Implant frozen embryo</DialogTitle>
          <DialogDescription>
            Promote {embryoLabel ?? 'the selected embryo'} from frozen to
            implanted. Transfer date and site come from the active session.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="implant-frozen-straw">New straw #</Label>
            <Input
              id="implant-frozen-straw"
              type="number"
              value={newStrawNumber}
              onChange={(e) => setNewStrawNumber(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Calf type</Label>
            <StringMapDropdown
              attributeName={StringMapAttributeName.CALF_TYPE}
              objectTypeCode={StringMapObjectTypeCode.CALF_TYPE}
              value={calfType ?? undefined}
              onChange={(e) =>
                setCalfType(typeof e.value === 'number' ? e.value : null)
              }
              disabled={isLoading}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Farm location</Label>
            <YardsAutoComplete
              defaultValueId={farmLocationId}
              onSelect={(e: { value: { id?: string } }) =>
                setFarmLocationId(e?.value?.id)
              }
              disabled={isLoading}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="implant-frozen-ehm">EHM date</Label>
            <Input
              id="implant-frozen-ehm"
              type="date"
              value={ehmDate}
              onChange={(e) => setEhmDate(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="implant-frozen-lot">Straw lot #</Label>
            <Input
              id="implant-frozen-lot"
              value={strawLotNo}
              onChange={(e) => setStrawLotNo(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="implant-frozen-incubator">Straw incubator #</Label>
            <Input
              id="implant-frozen-incubator"
              value={strawIncubatorNo}
              onChange={(e) => setStrawIncubatorNo(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="implant-frozen-thaw">Thaw thermos temp</Label>
            <Input
              id="implant-frozen-thaw"
              value={thawThermosTemp}
              onChange={(e) => setThawThermosTemp(e.target.value)}
              disabled={isLoading}
            />
          </div>
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || isLoading}
          >
            {isLoading ? 'Implanting…' : 'Implant'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
